import { uploadBytesResumable, uploadString, getDownloadURL } from 'firebase/storage';

const isReactNative = () => {
    // Expo/RN suele exponer `navigator.product === 'ReactNative'` pero no siempre.
    if (typeof navigator !== 'undefined' && navigator?.product === 'ReactNative') return true;
    // Hermes
    if (typeof globalThis !== 'undefined' && globalThis?.HermesInternal) return true;
    // RN bridge/hints (best-effort)
    if (typeof globalThis !== 'undefined' && typeof globalThis?.nativeCallSyncHook === 'function') return true;
    return false;
};

function base64ToUint8Array(base64) {
    const payload = base64.includes(',') ? base64.split(',')[1] : base64;
    // atob existe en RN (Hermes) y web. Si no existe, fallará de forma explícita.
    const binary = globalThis.atob(payload);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

function getStorageRefInfo(storageRef) {
    // Firebase Storage ref (web) suele exponer `.bucket` y `.fullPath`.
    const bucket = storageRef?.bucket || storageRef?._location?.bucket;
    const fullPath = storageRef?.fullPath || storageRef?._location?.path_;
    if (!bucket || !fullPath) return null;
    return { bucket, fullPath };
}

async function uploadBase64ViaRest(storageRef, base64, contentType) {
    const info = getStorageRefInfo(storageRef);
    if (!info) {
        throw new Error('uploadImageData: no pude derivar bucket/fullPath del storageRef.');
    }

    // Auth token (si existe sesión). Para reglas de Storage suele ser necesario.
    let idToken = null;
    try {
        const { firebase } = require('../config/configureFirebase');
        const user = firebase?.auth?.currentUser;
        if (user?.getIdToken) {
            idToken = await user.getIdToken();
        }
    } catch (_) {
        // ignore
    }

    const bytes = base64ToUint8Array(base64);
    const url =
        `https://firebasestorage.googleapis.com/v0/b/${encodeURIComponent(info.bucket)}` +
        `/o?uploadType=media&name=${encodeURIComponent(info.fullPath)}`;

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': contentType,
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: bytes,
    });

    if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Storage REST ${res.status}: ${txt || res.statusText}`.trim());
    }

    const json = await res.json().catch(() => null);
    const downloadTokens = json?.downloadTokens || json?.downloadTokens?.[0];
    if (downloadTokens) {
        return `https://firebasestorage.googleapis.com/v0/b/${info.bucket}/o/${encodeURIComponent(info.fullPath)}?alt=media&token=${downloadTokens}`;
    }
    // Si no hay token, igual devolvemos el getDownloadURL del SDK como fallback.
    return getDownloadURL(storageRef);
}

/**
 * Sube una imagen al storage soportando tanto strings base64 (recomendado en
 * React Native, donde Blob/fetch sobre file:// suele fallar) como Blob/Uint8Array
 * (web). Devuelve la URL de descarga.
 *
 * @param {object} storageRef Referencia de Firebase Storage.
 * @param {string|Blob|Uint8Array|ArrayBuffer} data
 * @param {object} [options]
 * @param {string} [options.contentType='image/jpeg']
 */
export async function uploadImageData(storageRef, data, options = {}) {
    if (!storageRef) {
        throw new Error('uploadImageData: storageRef es requerido.');
    }
    if (data === null || data === undefined || data === '') {
        throw new Error('uploadImageData: no se recibieron datos para subir.');
    }

    const contentType = options.contentType || 'image/jpeg';
    const metadata = { contentType };

    if (typeof data === 'string') {
        const isDataUrl = data.startsWith('data:');
        const format = isDataUrl ? 'data_url' : 'base64';
        // En RN, el SDK web de Storage puede fallar (Blob/ArrayBuffer). Usamos REST.
        if (isReactNative() && format === 'base64') {
            return await uploadBase64ViaRest(storageRef, data, contentType);
        }
        await uploadString(storageRef, data, format, metadata);
    } else if (data && typeof data === 'object' && (data.size > 0 || data.byteLength > 0)) {
        await uploadBytesResumable(storageRef, data, metadata);
    } else {
        throw new Error('uploadImageData: el formato de la imagen no es soportado o está vacío.');
    }

    return getDownloadURL(storageRef);
}

export default uploadImageData;
